/**
 * Pure dismissal-state logic for the "would you like to add anything
 * beforehand?" pre-shaping prompt (docs/design/gap-address-loop-2026-07-05.md,
 * ruling #3 — "There also should be a would you like to add anything
 * beforehand"). Shown once per job, right before the resume/cover-letter
 * shaping actions, offering either a jump into a gap-address conversation
 * (any unaddressed gap) or a plain dismissal.
 *
 * MIRRORS collapsibleSection.ts's OWN sessionStorage discipline byte-for-byte
 * (same hasWindow() guard, same try/catch-never-throws posture, same
 * per-job key) rather than inventing a second persistence pattern — this
 * module only adds the ONE new decision this prompt needs: "has this job's
 * prompt already been dismissed THIS tab session."
 *
 * NEVER A NAG LOOP: once dismissed for a job, it stays dismissed for the rest
 * of this tab session — reopening the job (or coming back to it after
 * addressing a gap) does not re-show it a second time unless the user
 * navigates away and back in a NEW tab/session.
 */

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function storageKey(jobId: string): string {
  return `throughline:pre-shaping-dismissed:v1:${jobId}`;
}

/** True if this job's pre-shaping prompt has already been dismissed this tab
 *  session. False (never shown / storage unavailable) is the honest default
 *  — a prompt this module has never recorded a dismissal for should show. */
export function isPreShapingDismissed(jobId: string): boolean {
  if (!hasSessionStorage()) return false;
  try {
    return window.sessionStorage.getItem(storageKey(jobId)) === '1';
  } catch {
    return false;
  }
}

/** Record that this job's pre-shaping prompt was dismissed (or acted on) for
 *  the rest of this tab session. Best-effort only — a storage failure just
 *  means the prompt may show again, never a thrown error over a cosmetic
 *  preference (same posture as collapsibleSection.ts's writeCollapseState). */
export function dismissPreShaping(jobId: string): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(storageKey(jobId), '1');
  } catch {
    // Best-effort only — see header comment.
  }
}
